/**
 * Journal entry detail as a side panel - inspecting a transaction should not cost you your place
 * in the table. Carries the audit trail, kept out of the main table where it would be noise.
 */
import React, { useState } from 'react';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { X, CheckCircle, Trash } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { api } from '../../../context/AuthContext';
import { cn } from '../../../lib/utils';
import { phaseMeta, phaseLabel, UNASSIGNED_PHASE } from '../../../lib/projectPhases';
import { money, STATUS_PILLS, SOURCE_LABELS } from './financeFormat';

const Field = ({ label, children }) => (
  <div className="py-2.5 border-b border-black/[0.04]">
    <p className="text-[10px] font-medium uppercase tracking-[0.12em] text-foreground-muted">{label}</p>
    <div className="text-sm mt-0.5">{children || '—'}</div>
  </div>
);

const timestamp = (value) => (value ? new Date(value).toLocaleString('en-GB') : null);

export const JournalEntryPanel = ({ entry, projectId, projectType, currency = 'EUR', onClose, onChanged, onEdit }) => {
  const [busy, setBusy] = useState(false);
  const pill = STATUS_PILLS[entry.status] || STATUS_PILLS.draft;
  const phase = entry.phase ? phaseMeta(entry.phase) : UNASSIGNED_PHASE;
  const isDraft = ['draft', 'pending_approval'].includes(entry.status);

  const act = async (action) => {
    if (action === 'delete' && !window.confirm('Delete this entry? This cannot be undone.')) return;
    setBusy(true);
    try {
      if (action === 'approve') {
        await api.post(`/admin/v2/finance/projects/${projectId}/entries/bulk`, {
          entry_ids: [entry.id],
          action: 'approve',
        });
        toast.success('Entry approved and now counted in actuals');
      } else {
        await api.delete(`/admin/v2/finance/entries/${entry.id}`);
        toast.success('Entry deleted');
      }
      onChanged?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Action failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="journal-entry-panel">
      <div className="absolute inset-0 bg-black/20" onClick={onClose} />
      <aside className="relative w-full max-w-[420px] h-full bg-white shadow-xl overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-black/[0.06] px-5 py-4 flex items-start justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-[0.14em] text-foreground-muted">Journal entry</p>
            <p className="text-xl font-semibold tabular-nums">{money(entry.amount, { currency, decimals: 2 })}</p>
            <p className="text-sm text-foreground-muted">{entry.description}</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-black/[0.05]" data-testid="journal-panel-close">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="px-5 py-2">
          <Field label="Status">
            <Badge variant="outline" className={cn('text-[10px] border', pill.className)}>{pill.label}</Badge>
            {isDraft && (
              <span className="ml-2 text-xs text-amber-700">Not counted in actuals until approved</span>
            )}
          </Field>
          <Field label="Date">{entry.entry_date}</Field>
          <Field label="Type"><span className="capitalize">{entry.entry_type}</span></Field>
          <Field label="Account">{entry.account_code} · {entry.account_name}</Field>
          <Field label="Category">{entry.category}</Field>
          <Field label="Phase">
            <Badge className={cn('text-[10px]', phase.chip)}>
              {entry.phase ? phaseLabel(entry.phase, projectType) : UNASSIGNED_PHASE.label}
            </Badge>
          </Field>
          <Field label="Counterparty">{entry.counterparty}</Field>
          <Field label="Reference">{entry.reference}</Field>
          <Field label="Source">
            {SOURCE_LABELS[entry.source] || entry.source}
            {entry.import_batch_id && <span className="text-foreground-muted"> · batch {entry.import_batch_id}</span>}
          </Field>
          {entry.external_id && <Field label="External transaction ID">{entry.external_id}</Field>}
          <Field label="Notes">{entry.notes}</Field>

          <div className="pt-4">
            <p className="text-[10px] font-medium uppercase tracking-[0.12em] text-foreground-muted mb-2">Activity</p>
            <ul className="space-y-1.5 text-xs text-foreground-muted" data-testid="journal-panel-audit">
              <li>Created {timestamp(entry.created_at) || '—'}{entry.created_by ? ` by ${entry.created_by}` : ''}</li>
              {entry.approved_at && <li>Approved {timestamp(entry.approved_at)}{entry.approved_by ? ` by ${entry.approved_by}` : ''}</li>}
              {entry.posted_at && <li>Posted {timestamp(entry.posted_at)}{entry.posted_by ? ` by ${entry.posted_by}` : ''}</li>}
              {(entry.edit_history || []).slice(-3).reverse().map((edit, index) => (
                <li key={index} data-testid={`journal-panel-edit-${index}`}>
                  Edited {timestamp(edit.at)}{edit.by ? ` by ${edit.by}` : ''}
                  {edit.changes && Object.keys(edit.changes).length > 0 && (
                    <span className="text-foreground-muted/80">
                      {' '}({Object.keys(edit.changes).join(', ')})
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="sticky bottom-0 bg-white border-t border-black/[0.06] px-5 py-3 flex items-center gap-2">
          {isDraft && (
            <Button size="sm" disabled={busy} onClick={() => act('approve')} data-testid="journal-panel-approve">
              <CheckCircle className="h-4 w-4 mr-1" /> Approve
            </Button>
          )}
          {onEdit && (
            <Button variant="outline" size="sm" onClick={() => onEdit(entry)} data-testid="journal-panel-edit">
              Edit
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            className="text-red-600 ml-auto"
            disabled={busy}
            onClick={() => act('delete')}
            data-testid="journal-panel-delete"
          >
            <Trash className="h-4 w-4 mr-1" /> Delete
          </Button>
        </div>
      </aside>
    </div>
  );
};

export default JournalEntryPanel;
